import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import WhatsNewModal from "../components/WhatsNewModal";
import RedeemCodeWidget from "../components/RedeemCodeWidget";
import BackerVisibilityCard from "../components/BackerVisibilityCard";
import MyReferralCard from "../components/MyReferralCard";
import TrialBanner from "../components/dashboard/TrialBanner";
import MenuGrid from "../components/dashboard/MenuGrid";
import { API } from "../constants";

export default function DashboardPage() {
  const { user, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [trial, setTrial] = useState(null);
  const inviteCode = searchParams.get("invite") || "";

  const loadTrial = React.useCallback(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios
      .get(`${API}/auth/trial-status`, { withCredentials: true, headers })
      .then((res) => setTrial(res.data))
      .catch(() => {
        /* not authenticated - ignore */
      });
  }, []);

  useEffect(() => {
    loadTrial();
  }, [loadTrial]);

  // Stripe success redirect: ?paid=1&session_id=cs_…  — confirm + flip user to lifetime.
  const paidToastFired = React.useRef(false);
  useEffect(() => {
    const paid = searchParams.get("paid");
    const sessionId = searchParams.get("session_id");
    if (paid !== "1" || !sessionId || paidToastFired.current) return;
    paidToastFired.current = true;
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        if (!mounted) return;
        const ok = res.data?.payment_status === "paid";
        // Defer past first paint so Sonner's Toaster listener is registered.
        setTimeout(() => {
          if (ok) toast.success("Welcome to lifetime! 🎉");
          else toast.info("Payment is still processing — refresh in a moment.");
        }, 150);
        if (ok && typeof checkAuth === "function") await checkAuth();
        if (ok) loadTrial();
      } catch (e) {
        setTimeout(() => {
          toast.error("Couldn't verify payment — please contact support if you were charged.");
        }, 150);
      } finally {
        // Strip the query params so a refresh doesn't re-toast.
        window.history.replaceState({}, "", "/dashboard");
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLifetimeGranted = () => {
    loadTrial();
    if (typeof checkAuth === "function") checkAuth();
  };

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h1 className="font-heading text-4xl font-bold text-white mb-2" data-testid="dashboard-title">
              Welcome, {user?.name || "User"}
            </h1>
            <p className="text-[#E8E4DC]">
              Subscription:{" "}
              <span className="text-[#A3E635] capitalize">
                {user?.subscription_status || user?.subscription_tier || "Free"}
              </span>
            </p>
          </div>

          <TrialBanner trial={trial} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <RedeemCodeWidget initialCode={inviteCode} onLifetimeGranted={onLifetimeGranted} />
            <MyReferralCard />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <BackerVisibilityCard user={user} />
          </div>

          <MenuGrid />
        </div>
      </div>
      <WhatsNewModal />
    </Layout>
  );
}
